import { makeRequest, toCorrectFormDataValue, } from '@/js/libs/api';
import * as Types from './types';
import { encodeBody, fromDateType as fromDate, toDateType as toDate, fromDateTimeType as fromDateTime, toDateTimeType as toDateTime, } from '@/js/libs/api-helpers';
export { fromDate, toDate, fromDateTime, toDateTime };
/** Fetches aggregated impression counts based on a specified time range, filters, and grouping dimensions. */ export const getImpressionAggregates = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    if (params.body === undefined) {
        throw new Error('Missing required body: body');
    }
    const path = '/api/ws-clickstream-analytics/impression-aggregates';
    const pathInternal = '/api/impression-aggregates';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.body !== undefined) {
        body = params.body;
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'POST',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: encodeBody(body),
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getImpressionAggregates.paramsOrder = [{ type: 'body', paramName: 'body' }];
/** Get report for bidding events */ export const getBiddingReport = async ({ onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    const path = '/api/ws-clickstream-analytics/bidding-report';
    const pathInternal = '/api/bidding-report';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getBiddingReport.paramsOrder = [];
/** Get report for BTA Feed Events */ export const getBtaFeedReport = async ({ onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    const path = '/api/ws-clickstream-analytics/bta-feed-report';
    const pathInternal = '/api/bta-feed-report';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getBtaFeedReport.paramsOrder = [];
/** Get report for BTA Feed Partners with RTB metrics by bidder */ export const getBtaFeedPartnersReport = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    const path = '/api/ws-clickstream-analytics/bta-feed-partners-report';
    const pathInternal = '/api/bta-feed-partners-report';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    {
        const value = queryDefaults === false ? params.page : (params.page ?? queryDefaults?.['page']);
        if (value !== undefined) {
            urlParams.append('page', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.pageSize : (params.pageSize ?? queryDefaults?.['pageSize']);
        if (value !== undefined) {
            urlParams.append('pageSize', value.toString());
        }
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getBtaFeedPartnersReport.paramsOrder = [{ type: 'parameters' }];
/**
 * Aggregates adView.duration viewability events at ad-impression grain in three stages. watched_ms is a
 * cumulative per-view timer, so each (view_key, view_number) view is collapsed with MAX; the views of one
 * view_key are summed into that ad impression's time on ad in seconds; the impressions are then grouped per
 * day / tag_id / creative_id / page_domain. One ad impression is one viewed ad render, so impressionCount is
 * additive across those dimensions.
 * Events carrying none of ad_id, auction, eaid, placement and cacheb have no view_key, cannot be tied to a
 * render, and are excluded; the query logs how many were dropped on every run.
 * minTimeOnAdSeconds / maxTimeOnAdSeconds drop ad impressions outside the band, so impressionCount, viewCount,
 * totalTimeOnAdSeconds, minTimeOnAdSeconds and maxTimeOnAdSeconds all describe one trimmed population.
 * excludedImpressionCount says how many impressions the band removed, and viewCountRaw /
 * totalTimeOnAdSecondsRaw are the same metrics over the untrimmed population, so the band is auditable and can
 * be retuned. Seconds are returned unrounded.
 * Returns raw ad-server ids (creative_id); the ws-bi report batch job resolves creative_id to ad-console
 * entities.
 * Scope is viewed impressions only: an ad that rendered but never entered the viewport emits no
 * adView.duration, so it is absent and every metric here reads below ad-server impressions.
 * day is the UTC day - ClickstreamEventConsumer derives event_date at UTC and the ingestion job carries it
 * through - so a day cuts at 01:00-02:00 Swiss/German local time and shifts ~1-2 hours of late-evening
 * traffic against Adomik/GAM reporting. Kept for consistency with the other clickstream reports and for
 * event_date partition pruning.
 * Transitional: time on ad is intended to move onto campaign positions directly, retiring this report type,
 * which is why no materialized view and no new BI operation stand behind it.
 */ export const getAdEngagementReport = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    if (params.body === undefined) {
        throw new Error('Missing required body: body');
    }
    const path = '/api/ws-clickstream-analytics/ad-engagement-report';
    const pathInternal = '/api/ad-engagement-report';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.body !== undefined) {
        body = params.body;
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'POST',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: encodeBody(body),
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getAdEngagementReport.paramsOrder = [{ type: 'body', paramName: 'body' }];
/** Fetches aggregated header analytics including header loaded counts, page impressions, visitor counts, and session counts based on a specified time range, filters, and grouping dimensions. */ export const getHeaderAnalytics = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    if (params.body === undefined) {
        throw new Error('Missing required body: body');
    }
    const path = '/api/ws-clickstream-analytics/header-analytics';
    const pathInternal = '/api/header-analytics';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.body !== undefined) {
        body = params.body;
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'POST',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: encodeBody(body),
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getHeaderAnalytics.paramsOrder = [{ type: 'body', paramName: 'body' }];
/** Exports aggregated header analytics data as a CSV file, including header loaded counts, page impressions, visitor counts, and session counts based on a specified time range, filters, and grouping dimensions. */ export const exportHeaderAnalyticsCsv = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    if (params.body === undefined) {
        throw new Error('Missing required body: body');
    }
    const path = '/api/ws-clickstream-analytics/header-analytics/csv';
    const pathInternal = '/api/header-analytics/csv';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.body !== undefined) {
        body = params.body;
    }
    const headers = {};
    headers['Accept'] = 'application/octet-stream';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'POST',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: encodeBody(body),
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'blob',
        requestType: 'json',
    });
};
exportHeaderAnalyticsCsv.paramsOrder = [{ type: 'body', paramName: 'body' }];
/** Fetches aggregated ad impression analytics including ad loaded counts, impression counts, visitor counts, session counts, and average winning bids based on a specified time range, filters, and grouping dimensions. */ export const getAdImpressionAnalytics = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    if (params.body === undefined) {
        throw new Error('Missing required body: body');
    }
    const path = '/api/ws-clickstream-analytics/ad-impression-analytics';
    const pathInternal = '/api/ad-impression-analytics';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.body !== undefined) {
        body = params.body;
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    headers['Content-Type'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'POST',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: encodeBody(body),
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getAdImpressionAnalytics.paramsOrder = [{ type: 'body', paramName: 'body' }];
/** Fetches unique div IDs filtered by optional company IDs and header tag IDs. */ export const getDivIds = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    const path = '/api/ws-clickstream-analytics/div-ids';
    const pathInternal = '/api/div-ids';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    {
        const value = queryDefaults === false ? params.companyIds : (params.companyIds ?? queryDefaults?.['companyIds']);
        if (value !== undefined) {
            urlParams.append('companyIds', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.headerTagIds : (params.headerTagIds ?? queryDefaults?.['headerTagIds']);
        if (value !== undefined) {
            urlParams.append('headerTagIds', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.query : (params.query ?? queryDefaults?.['query']);
        if (value !== undefined) {
            urlParams.append('query', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.limit : (params.limit ?? queryDefaults?.['limit']);
        if (value !== undefined) {
            urlParams.append('limit', value.toString());
        }
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getDivIds.paramsOrder = [{ type: 'parameters' }];
/** Fetches all unique bidder codes stored in the system. */ export const getBidderCodes = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    const path = '/api/ws-clickstream-analytics/bidder-codes';
    const pathInternal = '/api/bidder-codes';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    {
        const value = queryDefaults === false ? params.query : (params.query ?? queryDefaults?.['query']);
        if (value !== undefined) {
            urlParams.append('query', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.limit : (params.limit ?? queryDefaults?.['limit']);
        if (value !== undefined) {
            urlParams.append('limit', value.toString());
        }
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getBidderCodes.paramsOrder = [{ type: 'parameters' }];
/**
 * Fetches unique system winners. For RTB winners, returns bidder codes.
 * For IO winners, returns company IDs with resolved company names.
 */ export const getSystemWinners = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    const path = '/api/ws-clickstream-analytics/system-winners';
    const pathInternal = '/api/system-winners';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    {
        const value = queryDefaults === false ? params.query : (params.query ?? queryDefaults?.['query']);
        if (value !== undefined) {
            urlParams.append('query', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.limit : (params.limit ?? queryDefaults?.['limit']);
        if (value !== undefined) {
            urlParams.append('limit', value.toString());
        }
    }
    {
        const value = queryDefaults === false ? params.winnerType : (params.winnerType ?? queryDefaults?.['winnerType']);
        if (value !== undefined) {
            urlParams.append('winnerType', value.toString());
        }
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getSystemWinners.paramsOrder = [{ type: 'parameters' }];
/**
 * Fetches system winners matching the supplied codes. For RTB winners the code is
 * the bidder code; for IO winners the code is the company ID (display name resolved
 * to the company name).
 */ export const getSystemWinnersByCodes = async ({ params, onUploadProgress, signal, baseURL, isInternal, headers: headersParam = {}, queryDefaults, ...otherParams }) => {
    if (params.codes === undefined) {
        throw new Error('Missing required parameter: codes');
    }
    const path = '/api/ws-clickstream-analytics/system-winners/by-codes';
    const pathInternal = '/api/system-winners/by-codes';
    let body = {};
    const formData = new FormData();
    const urlParams = new URLSearchParams();
    if (params.codes !== undefined) {
        params.codes.forEach((item) => urlParams.append('codes', item.toString()));
    }
    const headers = {};
    headers['Accept'] = 'application/json';
    return makeRequest({
        ...otherParams,
        method: 'GET',
        url: isInternal ? pathInternal : path,
        params: urlParams,
        data: {},
        onUploadProgress,
        signal,
        baseURL: baseURL || undefined,
        headers: { ...headers, ...headersParam },
        responseTypes: 'json',
        requestType: 'json',
    });
};
getSystemWinnersByCodes.paramsOrder = [{ type: 'parameters' }];
export * from './types';
export * from './constants';
